import {axiosInstance} from './instance';

/** Bumped when the consent statement shown with the invite action changes (ZAP-81). */
export const INVITE_CONSENT_VERSION = '2026-10-v1';

export type InviteStatus = 'PENDING' | 'ACCEPTED' | 'EXPIRED' | 'REVOKED' | 'FAILED' | 'LOCKED';
export type AppAccessStatus = 'NOT_INVITED' | 'INVITED' | 'ACTIVE' | 'DISABLED';

/** Timestamps arrive as {epochSeconds, ...} objects, ISO strings or epoch numbers. */
type ApiTimestamp = {epochSeconds?: number} | string | number | null | undefined;

/** '+919876543210' -> '+91 ••••••3210', for showing the number an invite went to. */
export const maskPhone = (phone: string): string => {
  const hasIndiaCode = phone.startsWith('+91');
  const digits = (hasIndiaCode ? phone.slice(3) : phone).replace(/\D/g, '');
  const masked = digits.length <= 4 ? '••••' : '•'.repeat(digits.length - 4) + digits.slice(-4);
  return hasIndiaCode ? `+91 ${masked}` : masked;
};

export interface CreateInviteInput {
  /** E.164, for example +919876543210. */
  phoneNumber: string;
  targetId: string;
  /** The parent's acknowledgement of the consent statement; the API rejects false. */
  consentAccepted: boolean;
  consentVersion?: string;
}

export interface CreateInviteOutput {
  id: string;
  status: InviteStatus;
  expiresAt: ApiTimestamp;
  createdAt: ApiTimestamp;
  invitedById: string;
  role: string;
  phoneNumber: string;
}

export interface StudentInviteState {
  studentId: string;
  appAccess: AppAccessStatus;
  /** Status of the latest invite; absent if the student was never invited. */
  status?: InviteStatus;
  inviteId?: string;
  /** Masked, for example "+91 ••••••3210". */
  maskedPhone?: string;
  createdAt?: ApiTimestamp;
  expiresAt?: ApiTimestamp;
}

/** Epoch milliseconds for an API timestamp, or undefined. */
export const toEpochMs = (value: ApiTimestamp): number | undefined => {
  if (value == null) return undefined;
  if (typeof value === 'number') return value < 1e12 ? value * 1000 : value;
  if (typeof value === 'string') {
    const ms = Date.parse(value);
    return Number.isNaN(ms) ? undefined : ms;
  }
  return typeof value.epochSeconds === 'number' ? value.epochSeconds * 1000 : undefined;
};

/** Sends the student an invite link on WhatsApp. Sending again replaces a pending invite. */
export const createInvite = async (data: CreateInviteInput): Promise<CreateInviteOutput> => {
  // The server sends the WhatsApp message before it answers.
  const response = await axiosInstance.post<CreateInviteOutput>('/invites', data, {timeout: 30000});
  return response.data;
};

export const getStudentInvite = async (studentId: string): Promise<StudentInviteState> => {
  const response = await axiosInstance.get<StudentInviteState>(`/invites/students/${studentId}`);
  return response.data;
};
